/**
 * GDI Responsive Utilities
 *
 * Per-field helpers for the GDI StylesPerViewType pattern. While gdi-styles.js handles
 * bulk fieldMap-based resolution (used for primitives like Text/CTA), these helpers
 * are for layout blocks that need to read individual fields and transform them
 * (e.g. enum → flex value, number → CSS calc) before applying.
 *
 * Entries without ViewTypesToApply are defaults and apply to every web breakpoint.
 * Entries with ViewTypesToApply set their declared breakpoints, then inherit upward
 * in mobile-first order.
 */

import {fillGDIResponsiveInheritance, hasGDIViewTypes, parseGDIViewTypes} from './gdi-styles'

/* CMS may send view-type tokens with or without the "Web - " prefix. */
const VIEW_BP_MAP = {
    WebMobile: 'base',
    'Web - Mobile': 'base',
    WebTablet: 'md',
    'Web - Tablet': 'md',
    WebDesktop: 'lg',
    'Web - Desktop': 'lg'
}

const WEB_BREAKPOINTS = new Set(Object.values(VIEW_BP_MAP))
const ALL_BREAKPOINTS = ['base', 'md', 'lg']

export const parseResponsiveViewTypes = (viewTypes) =>
    parseGDIViewTypes(viewTypes).filter((bp) => WEB_BREAKPOINTS.has(bp))

/**
 * True for any value that should be respected as authored — filters out CMS empties
 * (`null`, `undefined`, `''`) and the "Default / Inherit" sentinel.
 */
export const hasValue = (v) =>
    v !== undefined &&
    v !== null &&
    (typeof v !== 'string' || (v.trim() !== '' && v.trim() !== 'Default / Inherit'))

const normalizeValue = (v) => (typeof v === 'string' ? v.trim() : v)

/**
 * Build a Chakra-style responsive object `{base, md, lg}` from a StylesPerViewType array
 * for a SINGLE field.
 *
 * @param {Array} stylesArr - Array from Optimizely
 * @param {(entry: Object) => any} getter - Reads/transforms a single field from one style entry
 * @param {any} fallback - Returned when the resolved object would be empty
 * @returns {Object|any} Chakra responsive object, or `fallback` when nothing was authored
 */
export const buildResponsive = (stylesArr, getter, fallback) => {
    if (!Array.isArray(stylesArr) || stylesArr.length === 0) return fallback
    const out = {}

    const defaults = []
    const targeted = []

    stylesArr.forEach((entry) => {
        const hasAuthoredViewTypes = hasGDIViewTypes(entry)
        const breakpoints = parseResponsiveViewTypes(entry?.ViewTypesToApply)

        if (!hasAuthoredViewTypes) {
            defaults.push(entry)
        } else if (breakpoints.length > 0) {
            targeted.push({entry, breakpoints})
        }
    })

    defaults.forEach((entry) => {
        const rawValue = getter(entry)
        if (!hasValue(rawValue)) return
        const value = normalizeValue(rawValue)
        ALL_BREAKPOINTS.forEach((bp) => {
            out[bp] = value
        })
    })

    targeted.forEach(({entry, breakpoints}) => {
        const rawValue = getter(entry)
        if (!hasValue(rawValue)) return
        const value = normalizeValue(rawValue)
        breakpoints.forEach((bp) => {
            out[bp] = value
        })
    })

    return Object.keys(out).length ? fillGDIResponsiveInheritance(out) : fallback
}

/**
 * Map every breakpoint in a responsive object through a lookup `table`.
 * If `responsive` is a scalar, returns `table[responsive]`.
 *
 * @example mapResponsiveEnum({base: 'Top', lg: 'Bottom'}, {Top: 'flex-start', Bottom: 'flex-end'})
 *          → {base: 'flex-start', lg: 'flex-end'}
 */
export const mapResponsiveEnum = (responsive, table) => {
    if (typeof responsive !== 'object' || responsive === null) {
        return table[responsive] ?? undefined
    }
    const out = {}
    for (const [bp, val] of Object.entries(responsive)) {
        if (table[val] !== undefined) out[bp] = table[val]
    }
    return Object.keys(out).length ? out : undefined
}

/**
 * Apply a unary `transform` to every breakpoint in a responsive object, or to a scalar.
 * Useful when you have a per-breakpoint number/string and need to wrap each value
 * (e.g. `n` → `span ${n}`, `n` → `calc(100% / ${n})`).
 *
 * @example mapResponsiveValue({base: 1, lg: 3}, (n) => `span ${n}`)
 *          → {base: 'span 1', lg: 'span 3'}
 */
export const mapResponsiveValue = (responsive, transform) => {
    if (typeof responsive !== 'object' || responsive === null) {
        return responsive === undefined || responsive === null ? undefined : transform(responsive)
    }
    return Object.fromEntries(Object.entries(responsive).map(([bp, v]) => [bp, transform(v)]))
}
